import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Linking} from 'react-native';
import {InAppBrowser} from 'react-native-inappbrowser-reborn';
import {useIsFocused} from '@react-navigation/native';
import {useTheme} from 'styled-components/native';
import BottomSheet from '../../Interface/BottomSheet';
import {Button} from '../../Interface/Forms/Buttons/Button';
import {useIdpayVerification} from '../../../context/IdpayVerification';
import {useRemoteConfig} from '../../../context/useRemoteConfig';
import {consultarStatusIdpay} from '../../../services/idpay';
import {
  ContentContainer,
  IconCircle,
  Icon,
  Title,
  Subtitle,
  SubtitleBold,
  WarningBox,
  WarningIcon,
  WarningText,
  ButtonsContainer,
} from './styles';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ResultState = 'success' | 'error' | null;

type GateProps = {
  scope?: 'session' | 'screen';
};

export function IdpayVerificationGate({scope = 'session'}: GateProps) {
  const isFocused = useIsFocused();
  const {pendencia, sheetShownRef} = useIdpayVerification();
  const [isOpen, setIsOpen] = useState(false);
  const shownHereRef = useRef(false);

  useEffect(() => {
    const jaExibido =
      scope === 'screen' ? shownHereRef.current : sheetShownRef.current;

    if (isFocused && pendencia && !jaExibido) {
      shownHereRef.current = true;
      sheetShownRef.current = true;
      setIsOpen(true);
    }
  }, [isFocused, pendencia, scope, sheetShownRef]);

  return (
    <IdpayVerificationBottomSheet
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    />
  );
}

export function IdpayVerificationBottomSheet({isOpen, onClose}: Props) {
  const theme = useTheme();
  const {pendencia, refresh} = useIdpayVerification();
  const {supportHelpText} = useRemoteConfig(isOpen);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<ResultState>(null);

  useEffect(() => {
    if (isOpen) {
      setResult(null);
    }
  }, [isOpen]);

  // O SDK/link da Unico só conduz o usuário pela jornada visual — a conclusão real
  // da verificação é sempre confirmada consultando o status no back-end, nunca pelo
  // retorno do navegador (regra de segurança do fluxo).
  const handleVerifyNow = useCallback(async () => {
    if (!pendencia) {
      return;
    }

    setVerifying(true);

    try {
      const available = await InAppBrowser.isAvailable();
      if (available) {
        await InAppBrowser.openAuth(pendencia.idpay_link, 'barataoapp://idpay-success', {
          modalEnabled: true,
          showTitle: true,
          enableUrlBarHiding: true,
          enableDefaultShare: false,
          forceCloseOnRedirection: true,
          toolbarColor: theme.colors.background,
          preferredBarTintColor: theme.colors.background,
          preferredControlTintColor: theme.colors.title,
        });
      } else {
        await Linking.openURL(pendencia.idpay_link);
      }
    } catch {}

    try {
      const status = await consultarStatusIdpay(pendencia._id.$oid);
      setResult(status.success ? 'success' : 'error');
    } catch {
      setResult('error');
    } finally {
      setVerifying(false);
      refresh();
    }
  }, [pendencia, refresh, theme]);

  const handleClose = useCallback(() => {
    setResult(null);
    onClose();
  }, [onClose]);

  const handleTalkToSupport = useCallback(() => {
    const supportPhone =
      supportHelpText.find(item =>
        ['phone', 'whatsapp', 'phone_number'].includes(item.type),
      )?.value ?? '';
    const supportPhoneDigits = supportPhone.replace(/\D/g, '');

    if (!supportPhoneDigits) {
      return;
    }

    Linking.openURL(
      `https://api.whatsapp.com/send/?phone=${supportPhoneDigits}&text&type=phone_number&app_absent=0`,
    );
  }, [supportHelpText]);

  if (result) {
    const isSuccess = result === 'success';

    return (
      <BottomSheet isOpen={isOpen} onClose={handleClose}>
        <ContentContainer>
          <IconCircle variant={isSuccess ? 'blue' : 'red'}>
            <Icon
              variant={isSuccess ? 'blue' : 'red'}
              name={isSuccess ? 'clock' : 'times-circle'}
            />
          </IconCircle>

          <Title>
            {isSuccess ? 'Verificação enviada' : 'Não conseguimos confirmar sua verificação'}
          </Title>
          <Subtitle>
            {isSuccess
              ? 'Sua verificação está sendo analisada por nossa equipe.'
              : 'Fale com o nosso suporte para que a gente possa verificar o seu caso e te ajudar a concluir a quitação do débito veicular.'}
          </Subtitle>

          <WarningBox variant={isSuccess ? 'blue' : 'red'}>
            <WarningIcon
              variant={isSuccess ? 'blue' : 'red'}
              name={isSuccess ? 'info-circle' : 'exclamation-triangle'}
            />
            <WarningText variant={isSuccess ? 'blue' : 'red'}>
              {isSuccess
                ? 'Assim que a análise for concluída, o débito veicular será quitado.'
                : 'Nosso suporte via WhatsApp pode verificar manualmente o status da sua verificação de identidade.'}
            </WarningText>
          </WarningBox>

          <ButtonsContainer>
            {isSuccess ? (
              <Button title="Entendi" onPress={handleClose} />
            ) : (
              <>
                <Button title="Falar com o suporte" onPress={handleTalkToSupport} />
                <Button title="Fechar" onPress={handleClose} variant="light" />
              </>
            )}
          </ButtonsContainer>
        </ContentContainer>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      <ContentContainer>
        <IconCircle>
          <Icon name="fingerprint" />
        </IconCircle>

        <Title>Falta 1 passo para quitar seu débito veicular</Title>
        <Subtitle>
          Para confirmar a <SubtitleBold>quitação do seu débito veicular</SubtitleBold>,
          é preciso fazer uma verificação de identidade rápida, com a sua
          câmera.
        </Subtitle>

        <WarningBox>
          <WarningIcon name="exclamation-triangle" />
          <WarningText>
            Enquanto a verificação não for concluída, seu débito veicular
            continua pendente de quitação.
          </WarningText>
        </WarningBox>

        <ButtonsContainer>
          <Button
            title="Verificar agora"
            onPress={handleVerifyNow}
            enabled={!!pendencia}
            loading={verifying}
          />
          <Button
            title="Verificar mais tarde"
            onPress={handleClose}
            enabled={!verifying}
            variant="light"
          />
        </ButtonsContainer>
      </ContentContainer>
    </BottomSheet>
  );
}
